import {
  closeIconModal,
  modal,
  modalContent,
  modalImage,
  modalImageContainer,
  fullSize,
  modalFooter,
} from './modal.css';
import { Modal as MuiModal } from '@mui/material';
import { Sprinkles, sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { ModalCta, ModalCtaProps } from './modalCta';
import {
  ModalTitle,
  ModalTypographyProps,
  ModalDescription,
} from './modalTypography';
import { ModalCloseButton } from './modalCloseIcon';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReactNode } from 'react';

export type PlainModalProps = React.PropsWithChildren<
  {
    isOpen: boolean;
    onClose: () => void;
    modalClassName?: string;
    modalContentClassName?: string;
    disableBackdropClick?: boolean;
    imgSrc?: string;
    centerContent?: boolean;
  } & Pick<Sprinkles, 'aspectRatio'>
>;

export const PlainModal = (p: PlainModalProps) => {
  const handleClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (!!p.disableBackdropClick && reason) {
      return;
    }
    p.onClose();
  };

  return (
    <MuiModal open={p.isOpen} onClose={handleClose}>
      <div
        data-testid="plain-modal-content"
        className={clsx(
          modal,
          sprinkles({ aspectRatio: p.aspectRatio }),
          p?.modalClassName,
        )}
      >
        {p.children}
      </div>
    </MuiModal>
  );
};

export type ModalProps = PlainModalProps &
  ModalCtaProps &
  ModalTypographyProps & {
    hideCloseButton?: boolean;
    customCloseIcon?: IconProp;
    footer?: ReactNode;
    footerClassName?: string;
    contentClassName?: string;
    largeTitle?: boolean;
    titleClassName?: string;
  };

export const Modal = (p: ModalProps) => (
  <PlainModal {...p} modalClassName={clsx(fullSize, p?.modalClassName)}>
    <div
      className={clsx(
        sprinkles({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 'xl',
          p: 'xl',
          bg: 'W-100',
          borderRadius: 'm',
          outline: 'none',
        }),
        p?.contentClassName,
      )}
    >
      {p.hideCloseButton ? null : (
        <ModalCloseButton
          className={closeIconModal}
          onClick={p.onClose}
          testId="modal"
          customIcon={p.customCloseIcon}
        />
      )}
      {p?.imgSrc ? (
        <div className={modalImageContainer}>
          <img
            src={p.imgSrc}
            className={modalImage}
            data-testid="modal-image"
          />
        </div>
      ) : null}
      {p.title ? (
        <ModalTitle
          title={p.title}
          className={clsx(
            sprinkles({ textAlign: p?.centerContent ? 'center' : 'left' }),
            p?.titleClassName,
          )}
        />
      ) : null}
      {p.description ? (
        <ModalDescription
          description={p.description}
          className={clsx(
            sprinkles({ textAlign: p?.centerContent ? 'center' : 'left' }),
          )}
        />
      ) : null}
      {p.children ? (
        <div
          className={clsx(
            p?.modalContentClassName,
            modalContent,
            sprinkles({ textAlign: p?.centerContent ? 'center' : 'left' }),
          )}
        >
          {p.children}
        </div>
      ) : null}
      <ModalCta
        primaryCta={p.primaryCta}
        secondaryCta={p.secondaryCta}
        columnCta={p.columnCta}
        reverseCta={p.reverseCta}
        stretchCta={p.stretchCta}
        testId="modal"
      />
    </div>
    {p?.footer ? (
      <div className={clsx(modalFooter, p?.footerClassName)}>{p.footer}</div>
    ) : null}
  </PlainModal>
);
